import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import { getCurrencySymbol } from '../utils/currency';
import './Repairs.css';

function Repairs({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const [repairs, setRepairs] = useState([]);
  const [cars, setCars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tags, setTags] = useState([]);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (tenantId) {
      loadRepairs();
      loadCars();
      loadCustomers();
      loadInvoices();
      loadUsers();
      loadRoles();
      loadTags();
      loadTenant();
    }
  }, [tenantId]);

  useEffect(() => {
    const repairId = searchParams.get('repairId');
    if (repairId && repairs.length > 0) {
      const repair = repairs.find(r => r.id === repairId);
      if (repair) {
        navigate(`/repairs/${repair.id}/edit`);
      }
    }
  }, [searchParams, repairs, navigate]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await api.get('/invoices', tenantId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const tenantUsers = data.filter(u => u.tenantId === tenantId);
        setUsers(tenantUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await api.get('/roles', tenantId);
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const getRoleName = (roleId) => {
    if (!roleId) return '-';
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : '-';
  };

  const getCarInfo = (carId) => {
    const car = cars.find(c => c.id === carId);
    return car ? `${car.make} ${car.model} (${car.licensePlate})` : '';
  };

  const getCustomerName = (carId) => {
    const car = cars.find(c => c.id === carId);
    if (!car || !car.customerId) return '';
    const customer = customers.find(c => c.id === car.customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : '';
  };

  const handleEdit = (repair) => {
    navigate(`/repairs/${repair.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Reparatur wirklich löschen?')) {
      try {
        await api.delete(`/repairs/${id}`, tenantId);
        loadRepairs();
      } catch (error) {
        console.error('Error deleting repair:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Unbekannter Fehler';
        showNotification('Fehler beim Löschen der Reparatur: ' + errorMessage, 'error');
      }
    }
  };

  const handleStatusUpdate = async (repairId, newStatus) => {
    try {
      await api.put(`/repairs/${repairId}`, { status: newStatus }, tenantId);
      loadRepairs();
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('Fehler beim Aktualisieren des Status: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  const handleCreateInvoice = async (repairId) => {
    try {
      const invoice = await api.post(`/invoices/from-repair/${repairId}`, {}, tenantId);
      loadRepairs();
      navigate(`/invoices?invoiceId=${invoice.id}`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      showNotification('Fehler beim Erstellen der Rechnung: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  const handleRecreateInvoice = async (repairId) => {
    if (window.confirm('Möchten Sie die Rechnung wirklich neu erstellen? Die alte Rechnung wird überschrieben.')) {
      try {
        const invoice = await api.post(`/invoices/from-repair/${repairId}`, {}, tenantId);
        loadRepairs();
        navigate(`/invoices?invoiceId=${invoice.id}`);
      } catch (error) {
        console.error('Error recreating invoice:', error);
        showNotification('Fehler beim Neuerstellen der Rechnung: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
      }
    }
  };

  return (
    <div className="repairs">
      <div className="page-header">
        <h1>Reparaturen</h1>
        <button onClick={() => navigate('/repairs/new')} className="btn-primary">
          Neue Reparatur
        </button>
      </div>

      <DataTable
        data={repairs}
        getRowClassName={(row) => {
          if (row.status === 'completed' && !row.invoiceId) {
            return 'row-status-completed-no-invoice';
          }
          if (row.status === 'pending') return 'row-status-pending';
          if (row.status === 'in_progress') return 'row-status-in-progress';
          if (row.status === 'completed') return 'row-status-completed';
          return '';
        }}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '120px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                {!row.invoiceId ? (
                  <IconButton
                    icon="invoice"
                    onClick={() => handleCreateInvoice(row.id)}
                    title="Rechnung erstellen"
                    className="primary"
                  />
                ) : (
                  <>
                    <IconButton
                      icon="preview"
                      onClick={() => navigate(`/repairs/${row.id}`)}
                      title="Reparatur anzeigen"
                      className="secondary"
                    />
                    <IconButton
                      icon="invoice"
                      onClick={() => navigate(`/invoices/${row.invoiceId}`)}
                      title="Rechnung anzeigen"
                      className="secondary"
                    />
                    <IconButton
                      icon="refresh"
                      onClick={() => handleRecreateInvoice(row.id)}
                      title="Rechnung neu erstellen"
                      className="secondary"
                    />
                  </>
                )}
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title={row.invoiceId ? "Kann nicht gelöscht werden, da bereits eine Rechnung vorhanden ist" : "Löschen"}
                  className="danger"
                  disabled={!!row.invoiceId}
                />
              </div>
            )
          },
          {
            key: 'repairNumber',
            header: 'ID',
            sortType: 'string',
            accessor: (row) => row.repairNumber || '',
            render: (row) => (
              <button 
                className="link-button" 
                onClick={() => navigate(`/repairs/${row.id}`)}
              >
                {row.repairNumber || '-'}
              </button>
            )
          },
          {
            key: 'car',
            header: 'Auto',
            sortType: 'string',
            accessor: (row) => getCarInfo(row.carId),
            render: (row) => {
              const car = cars.find(c => c.id === row.carId);
              const mainContent = car ? (
                <button 
                  className="link-button" 
                  onClick={() => navigate(`/cars/${car.id}`)}
                >
                  {getCarInfo(row.carId)}
                </button>
              ) : (
                <span>{getCarInfo(row.carId)}</span>
              );
              
              if (!row.tags || row.tags.length === 0) return mainContent;
              
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div>{mainContent}</div>
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                    {row.tags.map(tagId => {
                      const tag = tags.find(t => t.id === tagId);
                      if (!tag) return null;
                      return (
                        <span
                          key={tagId}
                          style={{
                            fontSize: '0.7rem',
                            padding: '0.15rem 0.4rem',
                            borderRadius: '20px',
                            backgroundColor: tag.color,
                            color: '#000',
                            border: `1.5px solid ${tag.color}`,
                            fontWeight: 600
                          }}
                        >
                          {tag.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            }
          },
          {
            key: 'customer',
            header: 'Kunde',
            sortType: 'string',
            accessor: (row) => getCustomerName(row.carId),
            render: (row) => {
              const car = cars.find(c => c.id === row.carId);
              if (car && car.customerId) {
                const customer = customers.find(c => c.id === car.customerId);
                if (customer) {
                  return (
                    <button 
                      className="link-button" 
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      {getCustomerName(row.carId)}
                    </button>
                  );
                }
              }
              return <span>{getCustomerName(row.carId) || '-'}</span>;
            }
          },
          {
            key: 'date',
            header: 'Datum',
            sortType: 'date',
            accessor: (row) => row.date,
            render: (row) => row.date ? new Date(row.date).toLocaleDateString('de-DE') : ''
          },
          {
            key: 'totalAmount',
            header: 'Betrag',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.totalAmount || 0,
            render: (row) => `${row.totalAmount?.toFixed(2)} ${currency}`
          },
          {
            key: 'assignedUser',
            header: 'Zugewiesen',
            sortType: 'string',
            accessor: (row) => {
              if (!row.assignedUserId) return '';
              const user = users.find(u => u.id === row.assignedUserId);
              if (!user) return '';
              const roleName = getRoleName(user.roleId);
              return roleName || user.userRole || user.username;
            },
            render: (row) => {
              if (!row.assignedUserId) return <span>-</span>;
              const user = users.find(u => u.id === row.assignedUserId);
              if (!user) return <span>-</span>;
              const roleName = getRoleName(user.roleId);
              return <span>{roleName || user.userRole || user.username}</span>;
            }
          },
          {
            key: 'status',
            header: 'Status',
            sortType: 'string',
            accessor: (row) => row.status,
            render: (row) => (
              <select
                value={row.status}
                onChange={(e) => handleStatusUpdate(row.id, e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                <option value="pending">Ausstehend</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="completed">Abgeschlossen</option>
              </select>
            )
          },
          {
            key: 'invoice',
            header: 'Rechnung',
            sortType: 'string',
            accessor: (row) => {
              if (row.invoiceId) {
                const invoice = invoices.find(inv => inv.id === row.invoiceId);
                return invoice?.invoiceNumber || '';
              }
              return '';
            },
            render: (row) => {
              if (row.invoiceId) {
                const invoice = invoices.find(inv => inv.id === row.invoiceId);
                if (invoice) {
                  return (
                    <button 
                      className="link-button" 
                      onClick={() => navigate(`/invoices/${row.invoiceId}`)}
                    >
                      {invoice.invoiceNumber}
                    </button>
                  );
                }
              }
              return <span>-</span>;
            }
          }
        ]}
        defaultSort={{ key: 'date', direction: 'desc' }}
        emptyMessage="Keine Reparaturen vorhanden"
      />
    </div>
  );
}

export default Repairs;

